import { Button } from '@/components/ui/button';
import { FileSpreadsheet, FileText } from 'lucide-react';
import { TargetCostData } from '@/types/target-cost';
import { exportTargetCostToCSV } from '@/utils/export/target-cost-csv';
import { exportTargetCostToPDF } from '@/utils/export/target-cost-pdf';
import { useToast } from '@/hooks/use-toast';
import { captureEvent, EVENTS, TOOL_IDS, TOOL_NAMES } from '@/lib/posthog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TargetCostExportButtonsProps {
  data: TargetCostData;
}

/**
 * Export buttons for Target Cost data.
 * Provides CSV and PDF export functionality with analytics tracking.
 */
export function TargetCostExportButtons({ data }: TargetCostExportButtonsProps) {
  const { toast } = useToast();

  const handleExportCSV = () => {
    try {
      exportTargetCostToCSV(data);

      captureEvent(EVENTS.TARGET_COST_EXPORTED, {
        tool_id: TOOL_IDS.TARGET_COST,
        tool_name: TOOL_NAMES[TOOL_IDS.TARGET_COST],
        export_format: 'csv',
        project_name: data.details.projectName,
      });

      toast({
        title: 'Success',
        description: 'Report exported to CSV successfully.',
        variant: 'default',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error
          ? error.message
          : 'Failed to export CSV report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      await exportTargetCostToPDF(data);

      captureEvent(EVENTS.TARGET_COST_EXPORTED, {
        tool_id: TOOL_IDS.TARGET_COST,
        tool_name: TOOL_NAMES[TOOL_IDS.TARGET_COST],
        export_format: 'pdf',
        project_name: data.details.projectName,
      });

      toast({
        title: 'Success',
        description: 'Report exported to PDF successfully.',
        variant: 'default',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error
          ? error.message
          : 'Failed to export PDF report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={handleExportCSV}
              aria-label="Export to CSV"
            >
              <FileSpreadsheet className="h-5 w-5" aria-hidden="true" />
              Export CSV
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Export your target cost data to a CSV file.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={handleExportPDF}
              aria-label="Export to PDF"
            >
              <FileText className="h-5 w-5" aria-hidden="true" />
              Export PDF
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Export your target cost data to a PDF file.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
